// Text catalog for the Kontakt / Contact page.
// The team portraits and section images live in src/pages/KontaktPage.tsx;
// only the role labels are translated here (names stay as-is).

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type TeamMember = { name: string; role: string; img: string };

type KontaktStrings = {
  meta: PageMeta;
  breadcrumbName: string;
  hero: { eyebrow: string; title: string; body: string };
  info: {
    eyebrow: string;
    heading: string;
    address: string;
    cvrLabel: string;
    bankName: string;
    bankRegLabel: string;
    bankAccountLabel: string;
  };
  form: {
    navn: string;
    email: string;
    mobil: string;
    firma: string;
    besked: string;
    submit: string;
    sending: string;
    success: string;
    errorPrefix: string;
    errorSuffix: string;
  };
  team: { eyebrow: string; heading: string; members: TeamMember[] };
  career: { heading: string; p1: string; p2: string; cta: string; href: string };
};

// Portrait paths are shared across locales; only role labels differ.
const portraits = {
  sonny: "/wp-content/uploads/Sonny_portraet_400x527.jpg",
  jonas: "/wp-content/uploads/Jonas_portraet_400x527.jpg",
  simon: "/wp-content/uploads/Simon_portraet_400x527.jpg",
  lars: "/assets-photos/lars-christiansen.png",
};

export const kontaktStrings: Record<Locale, KontaktStrings> = {
  da: {
    meta: {
      title: "Kontakt MI Support - Lad os tage en snak",
      description:
        "MI Support står klar til at hjælpe jer med jeres næste byggeprojekt. Kontakt os på info@misupport.dk eller +45 5555 1212.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg",
    },
    breadcrumbName: "Kontakt",
    hero: {
      eyebrow: "Vi glæder os til at høre fra dig",
      title: "Kontakt os",
      body: "MI Support står klar til at hjælpe jer med jeres næste projekt, uanset om det er en mindre byggeopgave eller et større prestigefuldt byggeprojekt. Vi har over 25 års erfaring med at styre større, komplicerede byggeprocesser, og vi er specialister i industrier som farma, kemi, energi og fødevarer. Kontakt os i dag, og lad os starte et nyt samarbejde.",
    },
    info: {
      eyebrow: "Skal vi starte et nyt samarbejde?",
      heading: "Lad os høre fra dig",
      address: "Korskildelund 4, 1., 2670 Greve",
      cvrLabel: "CVR",
      bankName: "Sydbank",
      bankRegLabel: "Reg.nr.",
      bankAccountLabel: "Kontonr.",
    },
    form: {
      navn: "Navn*",
      email: "Email*",
      mobil: "Mobil",
      firma: "Firma",
      besked: "Besked*",
      submit: "Send din besked",
      sending: "Sender…",
      success: "Tak for din besked, vi vender hurtigst muligt tilbage.",
      errorPrefix: "Noget gik galt, og beskeden blev ikke sendt. Prøv igen, eller skriv direkte til ",
      errorSuffix: ".",
    },
    team: {
      eyebrow: "Hvem er vi?",
      heading: "Ledelsen i MI Support",
      members: [
        { name: "Sonny Christensen", role: "Stifter & ejer", img: portraits.sonny },
        { name: "Jonas Terkild Christensen", role: "IT & økonomi", img: portraits.jonas },
        { name: "Simon Terkild Christensen", role: "Byggeledelse & HR", img: portraits.simon },
        { name: "Lars Christiansen", role: "Byggeledelse & kvalitet", img: portraits.lars },
      ],
    },
    career: {
      heading: "Er du vores nye kollega?",
      p1: "I MI Support er vi altid på udkig efter nye, dygtige kollegaer, der har lyst til at blive en del af vores team. Vi har jævnligt ledige stillinger slået op, og ellers er du altid velkommen til at skrive dig op i vores CV-bank, som vi løbende screener for interessante profiler.",
      p2: "MI Support er en dynamisk arbejdsplads, hvor der ikke er langt fra tanke til handling. Vi tilbyder engagerede og gode kollegaer med fokus på udvikling og faglig sparring. Herudover har vi gode og stabile arbejdsvilkår, og vi lægger vægt på et rart arbejdsmiljø med hyppige sociale arrangementer.",
      cta: "Se vores jobmuligheder",
      href: "https://misupport.career.emply.com/da/ledige-stillinger",
    },
  },

  en: {
    meta: {
      title: "Contact MI Support - Let's have a chat",
      description:
        "MI Support is ready to help you with your next construction project. Contact us at info@misupport.dk or +45 5555 1212.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg",
    },
    breadcrumbName: "Contact",
    hero: {
      eyebrow: "We look forward to hearing from you",
      title: "Contact us",
      body: "MI Support is ready to help you with your next project, whether it's a smaller construction task or a larger, prestigious building project. We have more than 25 years of experience managing large, complex construction processes, and we are specialists in industries such as pharma, chemicals, energy and food. Contact us today, and let's start a new collaboration.",
    },
    info: {
      eyebrow: "Shall we start a new collaboration?",
      heading: "Let us hear from you",
      address: "Korskildelund 4, 1., 2670 Greve, Denmark",
      cvrLabel: "VAT no.",
      bankName: "Sydbank",
      bankRegLabel: "Reg. no.",
      bankAccountLabel: "Account no.",
    },
    form: {
      navn: "Name*",
      email: "Email*",
      mobil: "Mobile",
      firma: "Company",
      besked: "Message*",
      submit: "Send your message",
      sending: "Sending…",
      success: "Thank you for your message, we will get back to you as soon as possible.",
      errorPrefix: "Something went wrong and the message was not sent. Please try again, or write directly to ",
      errorSuffix: ".",
    },
    team: {
      eyebrow: "Who are we?",
      heading: "The management at MI Support",
      members: [
        { name: "Sonny Christensen", role: "Founder & owner", img: portraits.sonny },
        { name: "Jonas Terkild Christensen", role: "IT & finance", img: portraits.jonas },
        { name: "Simon Terkild Christensen", role: "Construction management & HR", img: portraits.simon },
        { name: "Lars Christiansen", role: "Construction management & quality", img: portraits.lars },
      ],
    },
    career: {
      heading: "Are you our new colleague?",
      p1: "At MI Support we are always on the lookout for new, skilled colleagues who want to become part of our team. We regularly post vacancies, and otherwise you are always welcome to add yourself to our CV bank, which we continuously screen for interesting profiles.",
      p2: "MI Support is a dynamic workplace where it's not far from thought to action. We offer engaged and good colleagues with a focus on development and professional sparring. In addition, we have good and stable working conditions, and we place great emphasis on a pleasant working environment with frequent social events.",
      cta: "See our job opportunities",
      href: "https://misupport.career.emply.com/da/ledige-stillinger",
    },
  },

  de: {
    meta: {
      title: "Kontakt zu MI Support - Sprechen Sie mit uns",
      description:
        "MI Support steht bereit, Ihnen bei Ihrem nächsten Bauprojekt zu helfen. Kontaktieren Sie uns unter info@misupport.dk oder +45 5555 1212.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg",
    },
    breadcrumbName: "Kontakt",
    hero: {
      eyebrow: "Wir freuen uns, von Ihnen zu hören",
      title: "Kontaktieren Sie uns",
      body: "MI Support steht bereit, Ihnen bei Ihrem nächsten Projekt zu helfen – ob es sich um eine kleinere Bauaufgabe oder ein größeres, prestigeträchtiges Bauprojekt handelt. Wir haben über 25 Jahre Erfahrung in der Steuerung größerer, komplexer Bauprozesse und sind Spezialisten in Branchen wie Pharma, Chemie, Energie und Lebensmittel. Kontaktieren Sie uns noch heute, und lassen Sie uns eine neue Zusammenarbeit beginnen.",
    },
    info: {
      eyebrow: "Sollen wir eine neue Zusammenarbeit beginnen?",
      heading: "Lassen Sie uns von Ihnen hören",
      address: "Korskildelund 4, 1., 2670 Greve, Dänemark",
      cvrLabel: "USt-IdNr.",
      bankName: "Sydbank",
      bankRegLabel: "BLZ",
      bankAccountLabel: "Kontonr.",
    },
    form: {
      navn: "Name*",
      email: "E-Mail*",
      mobil: "Mobil",
      firma: "Firma",
      besked: "Nachricht*",
      submit: "Senden Sie Ihre Nachricht",
      sending: "Wird gesendet…",
      success: "Vielen Dank für Ihre Nachricht, wir melden uns schnellstmöglich zurück.",
      errorPrefix: "Etwas ist schiefgelaufen und die Nachricht wurde nicht gesendet. Bitte versuchen Sie es erneut, oder schreiben Sie direkt an ",
      errorSuffix: ".",
    },
    team: {
      eyebrow: "Wer sind wir?",
      heading: "Die Geschäftsführung bei MI Support",
      members: [
        { name: "Sonny Christensen", role: "Gründer & Inhaber", img: portraits.sonny },
        { name: "Jonas Terkild Christensen", role: "IT & Finanzen", img: portraits.jonas },
        { name: "Simon Terkild Christensen", role: "Baumanagement & HR", img: portraits.simon },
        { name: "Lars Christiansen", role: "Baumanagement & Qualität", img: portraits.lars },
      ],
    },
    career: {
      heading: "Sind Sie unser neuer Kollege?",
      p1: "Bei MI Support sind wir immer auf der Suche nach neuen, qualifizierten Kollegen, die Teil unseres Teams werden möchten. Wir schreiben regelmäßig offene Stellen aus, und ansonsten sind Sie jederzeit herzlich eingeladen, sich in unsere CV-Datenbank einzutragen, die wir laufend nach interessanten Profilen screenen.",
      p2: "MI Support ist ein dynamischer Arbeitsplatz, an dem es nicht weit vom Gedanken zur Handlung ist. Wir bieten engagierte und gute Kollegen mit Fokus auf Entwicklung und fachlichen Austausch. Darüber hinaus haben wir gute und stabile Arbeitsbedingungen und legen Wert auf ein angenehmes Arbeitsumfeld mit häufigen sozialen Veranstaltungen.",
      cta: "Sehen Sie unsere Stellenangebote",
      href: "https://misupport.career.emply.com/da/ledige-stillinger",
    },
  },
};
